/**
 * IsobarGLLayer.ts — خطوط تساوي الضغط كطبقة WebGL مستقلّة، تُرسم فوق **أي** حقل.
 *
 * ── لماذا طبقة مستقلّة لا فرعٌ في شيدر الضغط ────────────────────────────────
 *
 * الخطوط وُلِدت داخل شيدر طبقة الضغط (وهناك موضعها عند Zoom Earth: لا تعرض خطوطاً
 * إلّا على خريطة الضغط). لكن الخطوط عندنا مفتاحٌ عامّ يظهر فوق الرياح والحرارة
 * والأمطار كذلك، وذلك يستحيل من داخل شيدر الحقل: نسيج الحقل حينها حرارةٌ لا ضغط.
 *
 * فالمخرج طبقةٌ تقرأ نسيج الضغط بنفسها وترسم **الخطّ وحده** — شفّافة في كل بكسل
 * عداه. وبهذا يبقى مصدرٌ واحد للخوارزمية (نفس التمويه ونفس المستويات) مهما كان
 * الحقل الملوَّن تحتها، ويستحيل أن تفترق الخطوط عن نفسها بين طبقة وأخرى.
 *
 * ── وكيف تُرسم ──────────────────────────────────────────────────────────────
 *
 * لا تُتَّجه هندسة ولا تُبنى GeoJSON. لكل بكسل: يُموَّه الحقل بنواة ثنائية المقياس،
 * ثم تُقاس المسافة إلى أقرب مستوى كنتور **بالبكسل** عبر ميل الحقل. فيخرج خطّ عرضه
 * ثابت عند كل تقريب، مضادّ للتسنّن مجّاناً، ولا يمكن بناءً أن ينقطع أو «يظهر مرّة
 * ويختفي مرّة» — وهذان بالضبط عيبا مسار Marching Squares الذي حلّ هذا محلّه.
 */

import { MercatorCoordinate, type CustomLayerInterface, type CustomRenderMethodInput, type Map as MaplibreMap } from 'maplibre-gl';
import { type GLContext, createProgram, getLocations, createTexture, bindTexture } from './glUtils';
import { loadRasterImage } from '../../../services/rasterImageCache';
import { VALUE_RANGES } from './weatherTextures';
import { BLUR_STEP_FRACTION, blurStepScale, isobarInterval } from '../pressureAnnotations';

const VERTEX_SRC = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
varying vec2 v_merc;
void main() { v_merc = a_pos; gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0); }`;

/*
 * (تنبيه لمن يحرّر تعليقات هذا الشيدر: لا تستعمل علامة الاقتباس المائلة التي تُحيط
 *  بالشيفرة في ماركداون. النصّ كلّه داخل template literal في JS، فأيّ علامة منها
 *  تُنهي السلسلة وتُسقط الملفّ بمئة خطأ نحويّ بعيد عن موضع السبب.)
 */
const FRAGMENT_SRC = `
precision highp float;
varying vec2 v_merc;
uniform sampler2D u_value;
uniform sampler2D u_value2;
uniform float u_blend;
uniform float u_p16;         // النسيج نظير 16-بت (‏R=الأعلى، G=الأدنى)
uniform float u_interval;    // خطوة الكنتور بالهكتوباسكال
uniform float u_base;        // إزاحة طور تُنزل المستويات على مضاعفات الخطوة
uniform float u_span;        // مدى الترميز (vmax - vmin)
uniform float u_radius;      // نصف قطر التمويه بالبكسل الفيزيائي
uniform float u_step;        // نفس نصف القطر بوحدة مركاتور
uniform vec3 u_color;        // لون الخطّ
uniform float u_opacity;
const float PI = 3.141592653589793;

/**
 * فكّ قيمة الخليّة: الرمادي 8-بت يعطي R، ونظير 16-بت يعطي (R*256 + G)/65535.
 * الطرفان مُطبَّعان على المدى نفسه بالضبط (‏255*257 = 65535)، فلا فرع في أي مكان آخر.
 */
float dec(vec4 c) {
    return mix(c.r, (c.r * 65280.0 + c.g * 255.0) / 65535.0, u_p16);
}

vec2 wrapUV(vec2 uv) { return vec2(fract(uv.x), clamp(uv.y, 0.0, 1.0)); }

/*
 * ── التمويه ثنائي المقياس والميل معاً ────────────────────────────────────────
 *
 * نواة غاوسية على شبكة 9×9 خطوة، تُقرأ مرّتين: ضيّقة (المركز والحلقة الأولى) وعريضة
 * (الشبكة كلّها). والفرق بينهما هو **حدّة** الحقل محلياً؛ يُعاد مضروباً في 1.315 حيث
 * يكون كبيراً فتبقى نواة المنخفض المداري — وهي بضع خلايا عرضاً — حادّةً بما يكفي
 * لتُغلق حولها حلقات، ويُترك حيث يكون صغيراً فيُمحى ضجيج التكميم في الحقول المسطّحة.
 *
 * والميل يخرج من النواة نفسها بلا قراءة إضافية: مشتقّةُ التفافٍ مع غاوسية = التفافٌ
 * مع مشتقّة الغاوسية، ومشتقّة الغاوسية «−(d/σ²)·G». فيكفي جمع v·w·d بجانب v·w،
 * ثم القسمة على العزم الثاني الفعلي للنواة (راجع أدناه). وهذا ليس اختصاراً بل
 * ضرورة: MapLibre يفتح سياق WebGL2، وفيه لا تُعرَض الإضافة
 * GL_OES_standard_derivatives لشيدرات GLSL ES 1.00 — فـdFdx تُسقط الترجمة كلّها.
 */
void isoBlur(sampler2D t, vec2 c, vec2 d, out vec2 nb, out vec2 gNarrow, out vec2 gBroad) {
    float v0 = dec(texture2D(t, wrapUV(c)));
    float nSum = v0, nW = 1.0, bSum = v0, bW = 1.0;
    vec2 nG = vec2(0.0), bG = vec2(0.0);
    for (int j = -4; j <= 4; j++) {
        for (int i = -4; i <= 4; i++) {
            if (i == 0 && j == 0) continue;
            vec2 o = vec2(float(i), float(j));
            float w = exp(-dot(o, o) / 8.0);
            float v = dec(texture2D(t, wrapUV(c + d * o)));
            bSum += v * w; bW += w; bG += v * w * o;
            if (abs(o.x) <= 1.0 && abs(o.y) <= 1.0) { nSum += v * w; nW += w; nG += v * w * o; }
        }
    }
    nb = vec2(nSum / nW, bSum / bW);
    /*
     * القسمة على **العزم الثاني الفعلي** لكل نواة، لا على تباين الغاوسية النظري (4).
     *
     * الاشتقاق «G'(d) = −(d/σ²)·G» يصحّ لغاوسية كاملة غير مبتورة. ونواتنا مبتورة عند
     * ±4 خطوات، والضيّقة مبتورة عند ±1 — فعزمها الثاني ليس 4 بل 3.428 و0.638 على
     * الترتيب (يُحسبان مباشرةً من الأوزان). واستعمال 4 يجعل الميل المقروء أصغر من
     * الحقيقي: بنسبة 17% في العريضة، و**ستّة أضعاف ونصف** في الضيّقة.
     *
     * والميل الأصغر يعني «هكتوباسكال أقلّ لكل بكسل»، أي أن نفس الـ1.25 بكسل تُقاس
     * على مدى أوسع من الحقل — فيخرج الخطّ سميكاً بقعةً بدل أن يكون خطّاً. وأسوأ ما
     * يظهر ذلك حيث يكون معامل الحدّة كبيراً، إذ يغلب هناك ميلُ النواة الضيّقة وهو
     * الأشدّ خطأً. وهذا بالضبط ما رُصد: خطوطٌ رفيعة في معظم الخريطة وبقعٌ في مواضع.
     */
    gNarrow = nG / (0.63834 * nW);
    gBroad = bG / (3.42826 * bW);
}

void main() {
    float u = fract(v_merc.x);
    float latRad = 2.0 * atan(exp(PI * (1.0 - 2.0 * v_merc.y))) - PI * 0.5;
    float v = (degrees(latRad) + 90.0) / 180.0;
    if (v < 0.0 || v > 1.0) discard;

    /*
     * خطوة النواة رأسياً تُضرب في |dv/dmercY| = 2·cos(lat): «v» خطّيّ في خطّ العرض
     * بينما مركاتور ليس كذلك. بدون هذا العامل تصير النواة بيضاويةً متزايدة الاستطالة
     * كلّما ارتفع العرض، فتُمسح المنظومات القطبية أفقياً وتبقى حادّةً رأسياً.
     */
    vec2 g = vec2(u_step, u_step * 2.0 * max(cos(latRad), 0.05));
    vec2 nb, gN, gB;
    isoBlur(u_value, vec2(u, v), g, nb, gN, gB);
    if (u_blend > 0.001) {
        vec2 nb2, gN2, gB2;
        isoBlur(u_value2, vec2(u, v), g, nb2, gN2, gB2);
        nb = mix(nb, nb2, u_blend); gN = mix(gN, gN2, u_blend); gB = mix(gB, gB2, u_blend);
    }
    float sharp = (nb.x - nb.y) * u_span;
    float k = 1.315 * smoothstep(1.0, 4.0, abs(sharp));
    float field = nb.y * u_span + sharp * k;
    // الميل بنفس تركيبة القيمة، ثم من «لكل خطوة» إلى «لكل بكسل».
    vec2 grad = (gB + (gN - gB) * k) * u_span;
    float h = max(length(grad) / max(u_radius, 1e-4), 1e-4);
    /*
     * «t» مسافةٌ بالبكسل إلى أقرب مستوى، فعرض الخطّ ثابت مهما بلغ التكبير وحوافّه
     * مضادّة للتسنّن بلا كلفة. و«fade» يُخفي الخطوط حيث تتقارب دون بضعة بكسلات
     * (الجبهات الحادّة والعروض القطبية) بدل أن تتحوّل إلى بقعة مصمتة.
     */
    float t = abs(mod(field + u_base, u_interval) - 0.5) / h;   // مسافةٌ بالبكسل
    float fade = smoothstep(1.5, 4.0, u_interval / h);
    float line = fade * (1.0 - smoothstep(0.0, 1.25, t)) * u_opacity;
    if (line <= 0.002) discard;
    // ألفا مضروبة مسبقاً — نفس اصطلاح بقيّة طبقات الطقس.
    gl_FragColor = vec4(u_color * line, line);
}`;

const LAT_LIMIT = 85.051129;

interface TexEntry { tex: WebGLTexture; ready: boolean; gen: number }

export class IsobarGLLayer implements CustomLayerInterface {
    public readonly id: string;
    public readonly type = 'custom' as const;
    public readonly renderingMode = '2d' as const;

    private map: MaplibreMap | null = null;
    private program: WebGLProgram | null = null;
    private loc: ReturnType<typeof getLocations> | null = null;
    private quadBuffer: WebGLBuffer | null = null;
    private destroyed = false;
    private loadGen = 0;

    /** حلقة نُسج صغيرة بمفتاح الرابط — إطاران معروضان وبعض الجوار للترجيع. */
    private pool = new Map<string, TexEntry>();
    private url = '';
    private url2 = '';
    private blend = 0;
    /** النسيج نظير 16-بت؟ يُستنتج من الرابط لا يُفترض. */
    private p16 = true;
    private color: [number, number, number] = [1, 1, 1];
    private opacity = 0.92;

    constructor(id: string, url: string) {
        this.id = id;
        this.url = url;
    }

    setUrl(url: string) {
        if (url === this.url) return;
        this.url = url;
        if (this.map && url) this.acquire(this.mapGL(), url);
        this.map?.triggerRepaint();
    }
    setNextUrl(url: string) {
        if (url === this.url2) return;
        this.url2 = url;
        if (this.map && url) this.acquire(this.mapGL(), url);
        this.map?.triggerRepaint();
    }
    setBlend(b: number) {
        const next = Math.max(0, Math.min(1, b));
        if (Math.abs(next - this.blend) < 0.001) return;
        this.blend = next;
        this.map?.triggerRepaint();
    }
    setP16(v: boolean) { if (this.p16 === v) return; this.p16 = v; this.map?.triggerRepaint(); }
    setColor(c: [number, number, number]) {
        if (c[0] === this.color[0] && c[1] === this.color[1] && c[2] === this.color[2]) return;
        this.color = c; this.map?.triggerRepaint();
    }
    setOpacity(o: number) { if (this.opacity === o) return; this.opacity = o; this.map?.triggerRepaint(); }

    private glRef: GLContext | null = null;
    private mapGL(): GLContext { return this.glRef!; }

    onAdd(map: MaplibreMap, gl: GLContext) {
        this.map = map;
        this.glRef = gl;
        this.program = createProgram(gl, VERTEX_SRC, FRAGMENT_SRC);
        this.loc = getLocations(gl, this.program, ['a_pos'],
            ['u_matrix', 'u_value', 'u_value2', 'u_blend', 'u_p16',
                'u_interval', 'u_base', 'u_span', 'u_radius', 'u_step', 'u_color', 'u_opacity']);

        // رباعي يغطّي عدّة نسخ عالم أفقياً — الطول يُلَفّ في الشيدر (fract) فتظهر الخطوط
        // في كل النسخ المكرّرة (renderWorldCopies) بلا فجوة عند خطّ التاريخ.
        const yN = MercatorCoordinate.fromLngLat({ lng: 0, lat: LAT_LIMIT }).y;
        const yS = MercatorCoordinate.fromLngLat({ lng: 0, lat: -LAT_LIMIT }).y;
        const N = 3;
        const x0 = -N, x1 = 1 + N;
        this.quadBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([x0, yN, x1, yN, x0, yS, x1, yS]), gl.STATIC_DRAW);

        if (this.url) this.acquire(gl, this.url);
        if (this.url2) this.acquire(gl, this.url2);
    }

    onRemove(_map: MaplibreMap, gl: GLContext) {
        this.destroyed = true;
        if (this.program) gl.deleteProgram(this.program);
        if (this.quadBuffer) gl.deleteBuffer(this.quadBuffer);
        for (const e of this.pool.values()) gl.deleteTexture(e.tex);
        this.pool.clear();
        this.program = this.quadBuffer = null;
        this.map = this.glRef = null;
    }

    private acquire(gl: GLContext, src: string): TexEntry {
        const existing = this.pool.get(src);
        if (existing) {
            // إعادة الإدراج = أحدث استعمالاً (‏Map يحفظ ترتيب الإدراج).
            this.pool.delete(src); this.pool.set(src, existing);
            return existing;
        }
        const entry: TexEntry = {
            tex: createTexture(gl, 1, 1, new Uint8Array([0, 0, 0, 0]), { filter: gl.LINEAR })!,
            ready: false, gen: ++this.loadGen,
        };
        this.pool.set(src, entry);
        this.evict(gl);
        this.load(gl, entry, src);
        return entry;
    }

    /** يطرد الأقدم استعمالاً فوق السعة — عدا الإطارين المعروضين الآن. */
    private evict(gl: GLContext) {
        while (this.pool.size > 6) {
            let victim: string | null = null;
            for (const key of this.pool.keys()) {
                if (key === this.url || key === this.url2) continue;
                victim = key; break;
            }
            if (!victim) break;
            const e = this.pool.get(victim)!;
            this.pool.delete(victim);
            gl.deleteTexture(e.tex);
        }
    }

    private load(gl: GLContext, entry: TexEntry, src: string) {
        const gen = entry.gen;
        const isCurrent = () => !this.destroyed && this.pool.get(src) === entry && entry.gen === gen;
        void loadRasterImage(src).then((img) => {
            if (!isCurrent()) return;
            try {
                gl.bindTexture(gl.TEXTURE_2D, entry.tex);
                gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                entry.ready = true;
                this.map?.triggerRepaint();
            } catch { /* تجاهل */ }
        }).catch(() => {
            /*
             * 404: لن يصير الملفّ موجوداً بإعادة الطلب، ولا سقوط هنا.
             *
             * توفّر نظير 16-بت يُحسم **لكل دورة** براية في meta.json تُكتب من وجود
             * `pressure16_000.png` — وهو والإطارات الباقية تكتبها الحلقة نفسها، فإمّا
             * أن تكون الدورة كلّها 16-بت أو لا شيء منها. أمّا ساعةٌ مفقودة داخل دورة
             * موجودة فهي «لم تُولَّد بعد» لا «غير مدعومة»، وعلاجها إبقاء آخر إطار
             * معروضاً — وهو ما يحدث تلقائياً إذ لا يُرسم شيء قبل جهوز الإطار.
             */
        });
    }


    private errorLogged = false;

    render(gl: GLContext, options: CustomRenderMethodInput) {
        try { this.renderInner(gl, options); }
        catch (e) {
            if (!this.errorLogged) { console.error(`IsobarGLLayer(${this.id}) render error:`, e); this.errorLogged = true; }
        }
    }

    private renderInner(gl: GLContext, options: CustomRenderMethodInput) {
        if (!this.program || !this.loc || !this.map) return;
        const cur = this.pool.get(this.url);
        if (!cur?.ready) return;                    // لا شيء يُرسم قبل جهوز الإطار
        const nxt = this.url2 ? this.pool.get(this.url2) : undefined;

        const z = this.map.getZoom();
        // الخطوة من `isobarInterval` لا من نسخةٍ محلّية: طبقة النصوص تُسمّي المستويات
        // بالدالّة نفسها، ورقمان متباعدان يعنيان أرقاماً تُكتب على خطوط لا وجود لها.
        const interval = isobarInterval(z);
        const [vmin, vmax] = VALUE_RANGES.pressure;
        // البكسل فيزيائي لا CSS: قياس عرض الخطّ يجري في فضاء المخزن، فخلط الوحدتين
        // يجعل الخطّ ضعف سماكته على شاشة عالية الكثافة.
        const canvas = this.map.getCanvas();
        const dpr = canvas.width / Math.max(1, canvas.clientWidth);
        /*
         * الخطوة كسرٌ ثابت من محيط الكرة (‏BLUR_STEP_FRACTION)، ومضاعِفها يتبع دقّة
         * النسيج (‏blurStepScale). القاعدتان مُصدَّرتان من `pressureAnnotations` لا
         * مكتوبتان هنا: النواة نفسها تُقيَّم مرّةً ثانيةً على المعالج لاختيار مواضع
         * H/L وتسميات القيم، ولو افترقت الخطوتان لأمكن أن يظهر حرفٌ حيث لا حلقة.
         *
         * ونصف القطر بالبكسل يُشتقّ من الخطوة لا العكس، فيبقى الاثنان متّسقين بالبناء.
         * وعرض الخطّ لا يتأثّر باتّساع النواة إطلاقاً: الميل يُقاس «لكل خطوة» ثم يُقسم
         * على نصف القطر ليصير «لكل بكسل» — فالذي يتغيّر هو تنعيمُ موضع الخطّ لا سماكته.
         */
        const worldPx = 512 * 2 ** z * dpr;
        const stepMerc = BLUR_STEP_FRACTION * blurStepScale(this.p16);
        const radiusPx = stepMerc * worldPx;

        gl.useProgram(this.program);
        gl.uniformMatrix4fv(this.loc.uniforms.u_matrix, false,
            new Float32Array(options.defaultProjectionData.mainMatrix as ArrayLike<number>));
        gl.uniform1f(this.loc.uniforms.u_p16, this.p16 ? 1 : 0);
        gl.uniform1f(this.loc.uniforms.u_interval, interval);
        // الخطّ يقع حيث «mod(field + base, interval) = 0.5»، و«field» مقيسٌ من vmin.
        // فالإزاحة التي تُنزل المستويات على المضاعفات الصحيحة (‏…1016، 1020…) هي
        // «(vmin + 0.5) mod interval» — حلّ المعادلة لا رقم سحريّ.
        gl.uniform1f(this.loc.uniforms.u_base, ((vmin + 0.5) % interval + interval) % interval);
        gl.uniform1f(this.loc.uniforms.u_span, vmax - vmin);
        gl.uniform1f(this.loc.uniforms.u_radius, radiusPx);
        gl.uniform1f(this.loc.uniforms.u_step, stepMerc);
        gl.uniform3f(this.loc.uniforms.u_color, this.color[0], this.color[1], this.color[2]);
        gl.uniform1f(this.loc.uniforms.u_opacity, this.opacity);
        // نمزج مع الإطار التالي فقط حين يكون جاهزاً — وإلّا الإطار الحالي وحده.
        gl.uniform1f(this.loc.uniforms.u_blend, nxt?.ready && this.blend > 0.001 ? this.blend : 0);

        bindTexture(gl, cur.tex, 0); gl.uniform1i(this.loc.uniforms.u_value, 0);
        // بلا إطار تالٍ جاهز نربط الحالي في الفتحة الثانية أيضاً: u_blend = 0 يُلغي أثره،
        // لكن ترك فتحة بلا نسيج صالح يرمي على بعض التعريضات.
        bindTexture(gl, (nxt?.ready ? nxt.tex : cur.tex), 1); gl.uniform1i(this.loc.uniforms.u_value2, 1);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.quadBuffer);
        gl.enableVertexAttribArray(this.loc.attributes.a_pos);
        gl.vertexAttribPointer(this.loc.attributes.a_pos, 2, gl.FLOAT, false, 0, 0);
        gl.disable(gl.CULL_FACE);
        gl.disable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
}
